/**
 * useFormPermissions Hook
 * React hook to check user's permissions for a form template
 */

import { useState, useEffect, useCallback } from 'react'
import { getCachedFormAccess, clearTemplateCache } from '../services/formPermissionsService'

/**
 * Hook to check user's permissions for a form template
 * @param {string} projectId - Project ID
 * @param {string} templateId - Template ID
 * @param {object} options - Options { enabled: boolean, refetchOnMount: boolean }
 * @returns {object} Permissions state and helpers
 */
export function useFormPermissions(projectId, templateId, options = {}) {
  const { enabled = true, refetchOnMount = false } = options

  const [permissions, setPermissions] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchPermissions = useCallback(async () => {
    if (!projectId || !templateId || !enabled) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const data = await getCachedFormAccess(projectId, templateId)

      setPermissions(data)
    } catch (err) {
      console.error('[useFormPermissions] Error fetching permissions:', err)
      setError(err.message || 'Failed to fetch permissions')
      // Set denied permissions on error
      setPermissions({
        hasAccess: false,
        hasReviewerAccess: false,
        permissions: [],
        accessLevels: {
          canView: false,
          canEdit: false,
          canApprove: false,
          canReject: false,
        },
      })
    } finally {
      setLoading(false)
    }
  }, [projectId, templateId, enabled])

  // Refetch permissions
  const refetch = useCallback(() => {
    // Clear cache for this template to force fresh fetch
    if (projectId && templateId) {
      clearTemplateCache(projectId, templateId)
    }
    return fetchPermissions()
  }, [projectId, templateId, fetchPermissions])

  // Initial fetch
  useEffect(() => {
    if (refetchOnMount) {
      refetch()
    } else {
      fetchPermissions()
    }
  }, [fetchPermissions, refetchOnMount])

  return {
    // Permission data
    permissions,
    hasAccess: permissions?.hasAccess ?? false,
    hasReviewerAccess: permissions?.hasReviewerAccess ?? false,
    accessLevels: permissions?.accessLevels ?? {
      canView: false,
      canEdit: false,
      canApprove: false,
      canReject: false,
    },

    // Individual permission checks (convenience)
    canView: permissions?.accessLevels?.canView ?? false,
    canEdit: permissions?.accessLevels?.canEdit ?? false,
    canApprove: permissions?.accessLevels?.canApprove ?? false,
    canReject: permissions?.accessLevels?.canReject ?? false,

    // Loading state
    loading,
    error,

    // Actions
    refetch,
  }
}

/**
 * Hook to check permissions for multiple templates
 * @param {string} projectId - Project ID
 * @param {Array<string>} templateIds - Array of template IDs
 * @returns {object} Map of template permissions
 */
export function useMultipleFormPermissions(projectId, templateIds = []) {
  const [permissionsMap, setPermissionsMap] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!projectId || !templateIds || templateIds.length === 0) {
      setLoading(false)
      return
    }

    const fetchAllPermissions = async () => {
      try {
        setLoading(true)
        setError(null)

        const results = await Promise.all(
          templateIds.map(async (templateId) => {
            try {
              const data = await getCachedFormAccess(projectId, templateId)
              return { templateId, data }
            } catch (err) {
              console.error(`[useMultipleFormPermissions] Error for ${templateId}:`, err)
              return {
                templateId,
                data: {
                  hasAccess: false,
                  hasReviewerAccess: false,
                  accessLevels: {
                    canView: false,
                    canEdit: false,
                    canApprove: false,
                    canReject: false,
                  },
                },
              }
            }
          })
        )

        const map = results.reduce((acc, { templateId, data }) => {
          acc[templateId] = data
          return acc
        }, {})

        setPermissionsMap(map)
      } catch (err) {
        console.error('[useMultipleFormPermissions] Error:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchAllPermissions()
  }, [projectId, templateIds])

  return {
    permissionsMap,
    loading,
    error,
    getPermissions: (templateId) => permissionsMap[templateId] || null,
  }
}

export default useFormPermissions
